import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Languages, Menu, MessageCircle, Moon, Sun, X } from "lucide-react";
import { goTo, useLang } from "@/i18n";
import { useTheme } from "@/theme";
import { CONTACT } from "@/data";
import { Logo } from "@/components/Logo";

const NAV = [
  { id: "services", key: "nav_services" },
  { id: "airports", key: "nav_airports" },
  { id: "about", key: "nav_about" },
  { id: "payments", key: "nav_payments" },
  { id: "contact", key: "nav_contact" },
];

export function Header() {
  const { t, lang, setLang } = useLang();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const jump = (id) => {
    setOpen(false);
    if (document.getElementById(id)) goTo(id);
    else navigate(`/#${id}`);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled || open ? "glass border-b border-blue-500/15 shadow-[0_8px_40px_rgba(2,6,17,0.6)]" : "bg-transparent"}`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-[72px] items-center justify-between gap-4">
          <button onClick={() => navigate("/")} className="shrink-0" data-testid="header-logo-button" aria-label="WAS home">
            <Logo />
          </button>

          <nav className="hidden lg:flex items-center gap-7" data-testid="header-nav">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`nav-${n.id}-link`}
                onClick={() => jump(n.id)}
                className="text-sm font-medium text-slate-300 hover:text-white transition-colors duration-200 relative after:absolute after:-bottom-1 after:start-0 after:h-px after:w-0 after:bg-blue-500 hover:after:w-full after:transition-all after:duration-300"
              >
                {t(n.key)}
              </button>
            ))}
            <button
              data-testid="nav-rahalty-link"
              onClick={() => { setOpen(false); navigate("/rahalty"); }}
              className="text-sm font-bold text-blue-400 hover:text-blue-300 transition-colors duration-200"
            >
              {t("nav_rahalty")}
            </button>
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              data-testid="theme-toggle-button"
              onClick={toggle}
              aria-label={t("theme_toggle")}
              className="rounded-full border border-blue-500/30 p-2 text-blue-300 hover:bg-blue-500/10 transition-colors duration-200"
            >
              {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
            </button>
            <button
              data-testid="language-toggle-button"
              onClick={() => setLang(lang === "ar" ? "en" : "ar")}
              className="flex items-center gap-1.5 rounded-full border border-blue-500/30 px-3 py-1.5 text-xs font-semibold text-blue-300 hover:bg-blue-500/10 transition-colors duration-200"
            >
              <Languages size={14} />
              {lang === "ar" ? "EN" : "عربي"}
            </button>
            <a
              data-testid="header-whatsapp-button"
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-1.5 rounded-full border border-emerald-500/40 px-3 py-1.5 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/10 transition-colors duration-200"
            >
              <MessageCircle size={14} />
              <span className="hidden xl:inline">{t("nav_whatsapp")}</span>
            </a>
            <button
              data-testid="header-quote-button"
              onClick={() => jump("contact")}
              className="hidden md:inline-flex rounded-full bg-blue-600 px-5 py-2 text-sm font-bold text-white hover:bg-blue-500 transition-colors duration-200 shadow-[0_0_24px_rgba(37,99,235,0.45)]"
            >
              {t("nav_quote")}
            </button>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(!open)}
              aria-label={open ? t("menu_close") : t("menu_open")}
              className="lg:hidden rounded-full border border-blue-500/30 p-2 text-blue-300"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass border-t border-blue-500/15 px-6 py-4" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`mobile-nav-${n.id}-link`}
                onClick={() => jump(n.id)}
                className="text-start py-3 text-base font-medium text-slate-200 border-b border-blue-500/10 last:border-0"
              >
                {t(n.key)}
              </button>
            ))}
            <button
              data-testid="mobile-nav-rahalty-link"
              onClick={() => { setOpen(false); navigate("/rahalty"); }}
              className="text-start py-3 text-base font-bold text-blue-400 border-b border-blue-500/10"
            >
              {t("nav_rahalty")}
            </button>
            <button
              data-testid="mobile-quote-button"
              onClick={() => jump("contact")}
              className="mt-3 rounded-full bg-blue-600 px-5 py-3 text-sm font-bold text-white"
            >
              {t("nav_quote")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
